import React from 'react';
import { Draft, Pick } from '../objects/draftTypes';

interface DraftBoardProps {
    draft: Draft;
    currentDrafter: string | null;
    isOwner: boolean;
    onEditPick: (pickNumber: number) => void;
    onPrefillPick: (pickNumber: number, drafter: string) => void;
    onClearPrefilledPick: (pickNumber: number) => void;
}

const DraftBoard: React.FC<DraftBoardProps> = ({
    draft,
    currentDrafter,
    isOwner,
    onEditPick,
    onPrefillPick,
    onClearPrefilledPick
}) => {
    const { drafter_order, num_rounds, picks } = draft;

    // Create a lookup for picks by pick_number
    const picksByNumber: Record<number, Pick> = {};
    picks.forEach(pick => {
        picksByNumber[pick.pick_number] = pick;
    });

    // Generate the full board
    const rows: { round: number; picks: { drafter: string; pickNumber: number; pick: Pick | null }[] }[] = [];

    let pickNumber = 1;
    for (let round = 1; round <= num_rounds; round++) {
        // Serpentine: odd rounds forward, even rounds reverse
        const roundOrder = round % 2 === 1
            ? drafter_order
            : [...drafter_order].reverse();

        const roundPicks = roundOrder.map(drafter => {
            const pick = picksByNumber[pickNumber] || null;
            const result = { drafter, pickNumber, pick };
            pickNumber++;
            return result;
        });

        rows.push({ round, picks: roundPicks });
    }

    const draftNotStarted = draft.status === 'waiting_for_invites';

    return (
        <div className="draft-board">
            <h3>Draft Board</h3>
            <div className="board-container">
                <table className="board-table">
                    <thead>
                        <tr>
                            <th className="round-header">Round</th>
                            {drafter_order.map(drafter => (
                                <th
                                    key={drafter}
                                    className={`drafter-header ${drafter === currentDrafter ? 'current' : ''}`}
                                >
                                    {drafter}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map(row => (
                            <tr key={row.round}>
                                <td className="round-cell">{row.round}</td>
                                {drafter_order.map(drafter => {
                                    const cellData = row.picks.find(p => p.drafter === drafter);
                                    const pick = cellData?.pick;
                                    const pickNum = cellData?.pickNumber;
                                    const isCurrent = !pick && drafter === currentDrafter;
                                    const isPrefilled = pick?.is_prefilled === true;

                                    return (
                                        <td
                                            key={`${row.round}-${drafter}`}
                                            className={`pick-cell ${pick ? (isPrefilled ? 'prefilled' : 'filled') : 'empty'} ${isCurrent ? 'current' : ''}`}
                                        >
                                            {pick ? (
                                                <div className="pick-content">
                                                    <span className={`pick-value ${isPrefilled ? 'prefilled-value' : ''}`}>
                                                        {pick.value}
                                                    </span>
                                                    {isOwner && isPrefilled && (
                                                        <button
                                                            className="clear-btn"
                                                            onClick={() => onClearPrefilledPick(pick.pick_number)}
                                                            title="Clear prefilled pick"
                                                        >
                                                            Clear
                                                        </button>
                                                    )}
                                                    {isOwner && !isPrefilled && (
                                                        <button
                                                            className="edit-btn"
                                                            onClick={() => onEditPick(pick.pick_number)}
                                                            title="Edit pick"
                                                        >
                                                            Edit
                                                        </button>
                                                    )}
                                                </div>
                                            ) : (
                                                <div className="pick-content">
                                                    <span className="pick-placeholder">
                                                        {isCurrent ? '...' : '-'}
                                                    </span>
                                                    {isOwner && pickNum && (draftNotStarted || draft.status === 'in_progress') && (
                                                        <button
                                                            className="prefill-btn"
                                                            onClick={() => onPrefillPick(pickNum, drafter)}
                                                            title="Prefill this pick"
                                                        >
                                                            Prefill
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default DraftBoard;
